import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { chain, groupBy, filter, includes, isEmpty, map } from 'lodash';
import { DateTime } from 'luxon';
import { Cushion, MaxWidth, Rectangle, Space, Shadow, Typography } from '@pitchero/react-ui';
import { FIXTURES_PROP_TYPE } from '../../../lib/prop-types/fixture';
import { withTranslation, Trans } from '../../../lib/i18n';
import FixtureOverview from '../../fixture/overview';
import DateHeaderBar from '../../fixture/date-header-bar';
import FixtureOverviewPlaceHolder from '../../fixture/overview-placeholder';
import MatchEventIcon from '../../match-centre/match-event-icon';
import ProfileFixturesFilter from './fixtures-filter';

const ProfileFixtures = ({ fixtures, isLoading, teamId, t }) => {
  const [filterValue, setFilterValue] = useState(0);

  const eventTypeFilters = chain(fixtures)
    .filter('events')
    .flatMap((fixture) => fixture.events.map((event) => ({ ...event, fixtureId: fixture.id })))
    .groupBy('eventTypeId')
    .map((eventsOfType) => ({
      id: eventsOfType[0].eventTypeId,
      label: eventsOfType[0].label,
      count: eventsOfType.length,
    }))
    .orderBy(['count', 'label'], ['desc', 'asc'])
    .value();

  eventTypeFilters.unshift({
    id: 0,
    label: t('common:view_all'),
    count: fixtures.length,
  });

  const filteredFixtures = filterValue
    ? filter(fixtures, (fixture) => {
        const eventTypeIds = fixture.events.map((event) => event.eventTypeId);
        return includes(eventTypeIds, filterValue);
      })
    : fixtures;

  const fixturesByMonth = groupBy(filteredFixtures, (fixture) =>
    DateTime.fromISO(fixture.dateTime).toFormat('yyyy-MM-01'),
  );

  const handleFilterChange = (event) => {
    setFilterValue(parseInt(event.target.value, 10));
  };

  const renderFixtureList = () => {
    if (isLoading) {
      return (
        <Cushion top="large">
          <Space bottom="small" component="div">
            <FixtureOverviewPlaceHolder />
          </Space>
          <Space bottom="small" component="div">
            <FixtureOverviewPlaceHolder />
          </Space>
          <Space bottom="small" component="div">
            <FixtureOverviewPlaceHolder />
          </Space>
        </Cushion>
      );
    }

    return (
      <Shadow>
        <Rectangle radius="rounded" style={{ overflow: 'hidden' }}>
          {map(fixturesByMonth, (monthFixtures, month) => (
            <div key={`${month}`}>
              <DateHeaderBar date={month} />
              {monthFixtures.map((fixture) => (
                <Rectangle key={fixture.id} fill="white" borderBottom="thinLightGrey">
                  <FixtureOverview fixture={fixture} teamId={teamId} useInternalTeamName />
                  {(!isEmpty(fixture.events) || fixture.performance) && (
                    <Cushion horizontal="medium">
                      <MaxWidth component="div" maxWidth={300} center>
                        <div
                          style={{
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderBottom: '8px solid #F7F7F7',
                            width: 0,
                            margin: '0 auto',
                          }}
                        />
                        <Space bottom="medium">
                          <Rectangle fill="alabaster" radius="rounded">
                            <Cushion horizontal="small" vertical="xsmall" component="div">
                              {fixture.performance && (
                                <Cushion vertical="xxsmall">
                                  <Typography
                                    preset="tag--large"
                                    align="center"
                                    component="div"
                                    color="dustygrey"
                                  >
                                    <Trans
                                      i18nKey="teams:fixture_performance"
                                      values={{ performance: fixture.performance }}
                                    >
                                      {`Performance - ${fixture.performance}`}
                                    </Trans>
                                  </Typography>
                                </Cushion>
                              )}
                              {!isEmpty(fixture.events) &&
                                fixture.events.map((event) => (
                                  <Cushion vertical="xxsmall" key={event.id}>
                                    <Typography
                                      color="dustygrey"
                                      preset="name"
                                      component="div"
                                      align="center"
                                    >
                                      <Cushion right="xsmall">
                                        <MatchEventIcon
                                          name={event.icon.name}
                                          color="dustygrey"
                                          topOffset={3}
                                          size={16}
                                        />
                                      </Cushion>
                                      {` ${event.label} (${event.time})`}
                                    </Typography>
                                  </Cushion>
                                ))}
                            </Cushion>
                          </Rectangle>
                        </Space>
                      </MaxWidth>
                    </Cushion>
                  )}
                </Rectangle>
              ))}
            </div>
          ))}
        </Rectangle>
      </Shadow>
    );
  };

  return (
    <>
      {eventTypeFilters.length > 1 && (
        <Space bottom="large" component="div">
          <ProfileFixturesFilter
            eventTypes={eventTypeFilters}
            selected={filterValue}
            onChange={handleFilterChange}
          />
        </Space>
      )}
      {renderFixtureList()}
    </>
  );
};

ProfileFixtures.propTypes = {
  fixtures: FIXTURES_PROP_TYPE,
  isLoading: PropTypes.bool.isRequired,
  teamId: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

ProfileFixtures.defaultProps = {
  fixtures: [],
};

export default withTranslation(['common'])(ProfileFixtures);
